import { formatDifferenceInHoursAndMinutes } from '@backoffice-frontend/common'
import { formatApproximateDifferenceInHoursAndMinutes } from '@backoffice-frontend/fleet-maps-common'
import { AreaType, FccWaypointState } from '@backoffice-frontend/graphql'
import {
  MoiaGrid,
  ReallocationIcon,
  useTheme,
} from '@backoffice-frontend/patterns'
import { keyframes } from '@emotion/react'
import {
  CoffeeIcon,
  HomeEnterIcon,
  MapPinIcon,
  WheelchairIcon,
} from '@moia-dev/pace-icons'
import { Tooltip, TooltipContent, TooltipTrigger } from '@moia-dev/pace-web'
import { Trans, useTranslation } from 'react-i18next'
import {
  VEHICLE_MISSION_WAYPOINT_DESTINATION_DIVIDER_HEIGHT,
  VEHICLE_MISSION_WAYPOINT_DIVIDER_HEIGHT,
  VEHICLE_MISSION_WAYPOINT_HEADER_HEIGHT,
  VEHICLE_MISSION_WAYPOINT_MARGIN_BOTTOM_HEIGHT,
} from '../constants'
import type { VehicleMissionFccUpcomingWaypointAction } from '../parseVehicle'
import { AdVehicleMissionWaypointActionStates } from './AdVehicleMissionWaypointActionStates/AdVehicleMissionWaypointActionStates'
import { getActionsStatus, isTripAction, type Waypoint } from './actionStatus'
import { VehicleMissionWaypointAction } from './VehicleMissionWaypointAction/VehicleMissionWaypointAction'
import { VehicleMissionWaypointDestination } from './VehicleMissionWaypointDestination/VehicleMissionWaypointDestination'

type VehicleMissionWaypointDetailsProps = {
  waypoint: Waypoint
  isCurrent: boolean
}

export const VehicleMissionWaypoint = ({
  waypoint,
  isCurrent,
}: VehicleMissionWaypointDetailsProps) => {
  const theme = useTheme()
  const { t } = useTranslation()

  const firstWaypointAction = waypoint.actions.at(0)
  const eta =
    firstWaypointAction?.estimatedAt &&
    new Date(firstWaypointAction.estimatedAt)
  const arrivalIn =
    eta && formatApproximateDifferenceInHoursAndMinutes(eta, new Date())
  const arrivedSince =
    waypoint.adStateUpdatedAt &&
    formatDifferenceInHoursAndMinutes(
      new Date(),
      new Date(waypoint.adStateUpdatedAt),
    )

  const waypointAllocation = (
    waypointAction: VehicleMissionFccUpcomingWaypointAction,
  ) => {
    switch (waypointAction.__typename) {
      case 'FccWaypointReallocationAction':
        return {
          name: t('adFleetMap_missionCard_rebalancingZone_label'),
          icon: <ReallocationIcon />,
        }
      case 'FccWaypointPickupAction':
        return {
          name: t('adFleetMap_missionCard_pickUp_label'),
          icon: <MapPinIcon />,
        }
      case 'FccWaypointDeliveryAction':
        return {
          name: t('adFleetMap_missionCard_dropOff_label'),
          icon: <MapPinIcon />,
        }
      case 'FccWaypointHubReturnAction':
        if (waypointAction.area?.type === AreaType.Hub) {
          return {
            name: t('common_hub_label'),
            icon: (
              <HomeEnterIcon
                css={{ color: theme.semantic.ColorContentReduced }}
              />
            ),
          }
        }
        if (
          waypointAction.area?.type === AreaType.InFieldBreak ||
          waypointAction.area?.type === AreaType.ShortBreak
        ) {
          return {
            name: t('common_breakPoint_label'),
            icon: <CoffeeIcon />,
          }
        }
        return {}
      default:
        return {}
    }
  }

  // Create Set of mission types without duplicates
  const waypointTypes = [...new Set(waypoint.actions.map(waypointAllocation))]

  // check if the mission types
  const hasMultipleWaypointTypes =
    waypointTypes.length > 1 &&
    waypointTypes[1]?.name !== waypointTypes[0]?.name

  // Checks if the waypointaction has a wheelchairuser
  const hasWheelchairUser = waypoint.actions.some(
    action => isTripAction(action) && action.seats.wheelchairs !== 0,
  )

  const adWaypointReached = waypoint.adState === FccWaypointState.Reached
  const actionsStatus = getActionsStatus(waypoint)

  return (
    <div css={{ backgroundColor: theme.semantic.ColorBackgroundAlternative }}>
      {/* Waypoint Header */}
      <MoiaGrid
        alignItems="end"
        gridGap={0}
        gridTemplateColumns={`${theme.space.Space4} 56px 1fr auto ${theme.space.Space4}`}
        gridTemplateRows={`${VEHICLE_MISSION_WAYPOINT_HEADER_HEIGHT}px`}
      >
        <div
          css={{
            gridColumnStart: 2,
            fontSize: 12,
            fontWeight: 400,
            color: isCurrent
              ? theme.semantic.ColorAccentDefault
              : theme.semantic.ColorContentReduced,
          }}
        >
          {isCurrent && (
            <>
              {t('common_now_label').toUpperCase()}
              <strong
                css={{
                  marginLeft: 1,
                  animation: `${keyframes`to { visibility: hidden; }`} 1.7s
                      steps(2, start) infinite`,
                }}
              >
                .
              </strong>
            </>
          )}
        </div>
        <div
          css={{
            fontSize: 14,
            display: 'flex',
            gap: theme.space.Space1,
          }}
        >
          {adWaypointReached
            ? t('adFleetMap_missionCard_stoppedAt_label')
            : firstWaypointAction?.__typename ===
                'FccWaypointReallocationAction'
              ? t('adFleetMap_missionCard_drivingTo_feminine_label')
              : t('adFleetMap_missionCard_drivingTo_masculine_label')}
          <Tooltip>
            <TooltipTrigger>
              <span css={{ fontWeight: 'bold' }}>
                {waypointTypes.at(0)?.name}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {t('adFleetMap_missionCard_routeProvider_label')}
              {': '} {waypoint.routeProvider ?? t('common_unknown_label')}
            </TooltipContent>
          </Tooltip>
          {hasMultipleWaypointTypes && (
            <>
              <span>{t('common_and_label')}</span>
              <span css={{ fontWeight: 'bold' }}>
                {waypointTypes.at(1)?.name}
              </span>
            </>
          )}
          {hasWheelchairUser && <WheelchairIcon height={20} width={20} />}
        </div>
        <div
          css={theme => ({
            alignItems: 'baseline',
            display: 'flex',
            fontSize: 14,
            marginLeft: 'auto',
            gap: theme.space.Space1,
          })}
        >
          <Trans
            components={[
              <span css={{ color: theme.semantic.ColorContentDefault }} />,
              <span css={{ fontWeight: 'bold', fontSize: 14 }} />,
            ]}
            shouldUnescape
          >
            {adWaypointReached
              ? t('adFleetMap_missionCard_for_label', { for: arrivedSince })
              : t('adFleetMap_missionCard_in_label', {
                  in: arrivalIn,
                })}
          </Trans>
        </div>
      </MoiaGrid>

      {/* Destination */}
      <VehicleMissionWaypointDestination
        waypoint={waypoint}
        waypointTypes={waypointTypes}
      />

      {/* Separtor only for Delivery & Pickup */}
      {firstWaypointAction && isTripAction(firstWaypointAction) && (
        <MoiaGrid
          gridGap={0}
          gridTemplateColumns={`${theme.space.Space4} 56px 4fr 3fr ${theme.space.Space4}`}
        >
          <div
            css={theme => ({
              borderBottom: `${VEHICLE_MISSION_WAYPOINT_DESTINATION_DIVIDER_HEIGHT}px solid ${theme.semantic.ColorAccessoryReduced}`,
              height: 0,
              gridColumnStart: 3,
              gridColumnEnd: -2,
            })}
          />
        </MoiaGrid>
      )}

      {/* Actions */}
      {waypoint.actions
        .filter(action => {
          if (isTripAction(action)) {
            return action.seats.seats > 0
          }
          return false
        })
        .map(waypointAction => {
          return (
            <VehicleMissionWaypointAction
              key={waypointAction.id}
              waypointAction={waypointAction}
            />
          )
        })}

      {/* AD Actions States */}
      {adWaypointReached && actionsStatus && (
        <AdVehicleMissionWaypointActionStates
          waypointActionsStatus={actionsStatus}
        />
      )}

      {/* Waypoint Divider */}
      <div
        css={theme => ({
          borderBottom: `${VEHICLE_MISSION_WAYPOINT_DIVIDER_HEIGHT}px solid ${theme.semantic.ColorAccessoryReduced}`,
          flexGrow: 1,
          height:
            VEHICLE_MISSION_WAYPOINT_DIVIDER_HEIGHT +
            VEHICLE_MISSION_WAYPOINT_MARGIN_BOTTOM_HEIGHT,
        })}
      />
    </div>
  )
}
